import { t } from "@travily/i18n";
import { AvatarPicker, type Selection } from "@ui/components/AvatarPicker";
import { Wizard, type WizardColor } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

export interface AccountPictureStepViewProps {
  /** Wizard color theme for this account type. */
  color: WizardColor;
  /** Stage illustration (the container imports the per-type asset and sizes it). */
  illustration: ReactNode;
  /** Stage heading, e.g. "Add a profile picture" / "Add a logo". */
  heading: string;
  /** Stage helper copy under the heading. */
  helper: string;
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Save and finish later"). */
  topBarActions: ReactNode;
  /** Advance the wizard — bound to both "Skip" and "Continue". */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Account profile type, drives the default-tile avatar color. */
  profileType: string;
  /** Account initial rendered as the default-tile avatar fallback. */
  initial: string | null;
  /** Controlled avatar-picker selection state. */
  selection: Selection;
  /** Preview URL for the upload slot (blob URL or existing image URL). */
  uploadPreview: string | null;
  /** Called when a file is chosen for upload. */
  onFileSelect: (file: File) => void;
  /** Called when the uploaded file is removed. */
  onRemove: () => void;
  /** Called when the avatar-picker selection changes. */
  onSelectionChange: (selection: Selection) => void;
}

/**
 * Shared "add a picture" creator stage rendered by the family, community and individual creators —
 * parameterized by wizard color, illustration and heading/helper copy.
 */
export function AccountPictureStepView({
  color,
  illustration,
  heading,
  helper,
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  profileType,
  initial,
  selection,
  uploadPreview,
  onFileSelect,
  onRemove,
  onSelectionChange,
}: AccountPictureStepViewProps) {
  return (
    <Wizard color={color} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.SecondaryButton onPress={onPrimary} mobileLabel={t("common.actions.skip")}>
        {t("common.actions.skip")}
      </Wizard.SecondaryButton>
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage illustration={illustration} heading={heading} helper={helper}>
        <div className="w-full max-w-md lg:max-w-lg">
          <AvatarPicker
            defaultTile={
              <AccountAvatar
                className="size-full text-4xl lg:text-5xl"
                account={{ profile_type: profileType, avatar: null, initial }}
              />
            }
            isLabelHidden
            label={t("components.views.accounts.profile_form.upload_profile_picture")}
            onFileSelect={onFileSelect}
            onRemove={onRemove}
            onSelectionChange={onSelectionChange}
            selection={selection}
            uploadPreview={uploadPreview}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
